import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {ActionButton} from '../../shared/components/ActionButton';
import {ServiceContainer} from '../../shared/di/ServiceContainer';
import {notificationService} from '../../shared/services/notificationService';
import {useAuthContext} from '../context/AuthContext';

interface TwoFactorScreenProps {
  onVerificationSuccess: () => void;
  onBackToLogin: () => void;
}

const CODE_LENGTH = 6;
const TIMER_DURATION = 300; // 5 minutos en segundos

export const TwoFactorScreen: React.FC<TwoFactorScreenProps> = ({
  onVerificationSuccess,
  onBackToLogin,
}) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSended, setCodeSended] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const codeInputRef = useRef<TextInput>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const authService = ServiceContainer.getInstance().getAuthService();
  const {user, logout, refreshAuth} = useAuthContext();

  useEffect(() => {
    loadTempToken();
    return () => {
      clearCountdown();
    };
  }, []);

  const loadTempToken = async () => {
    try {
      // Get temp_token from auth context
      const token = user?.temp_token || user?.token || null;
      setTempToken(token);
      if (!token) {
        notificationService.error(
          'No hay token disponible. Por favor, inicia sesión nuevamente.'
        );
        onBackToLogin();
      }
    } catch (error) {
      console.error('Error loading temp token:', error);
      onBackToLogin();
    }
  };

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    setTimeRemaining(TIMER_DURATION);
    clearCountdown();
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const getFormattedTime = (): string => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const isCodeExpired = (): boolean => {
    return timeRemaining === 0 && codeSended;
  };

  const canResendCode = (): boolean => {
    return isCodeExpired() || !codeSended;
  };

  const send2FACode = async (method: 'sms' | 'email') => {
    if (!tempToken) {
      notificationService.error(
        'No hay token disponible para enviar el código'
      );
      return;
    }

    setIsSendingCode(true);
    try {
      await authService.send2FACode({type: method}, tempToken);
      const methodDisplay = method === 'sms' ? 'SMS' : 'Email';
      notificationService.success(
        `Código de verificación enviado por ${methodDisplay}`
      );
      setCodeSended(true);
      startCountdown();
    } catch (error: any) {
      notificationService.error(
        error.message || 'Error al enviar el código de verificación'
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== CODE_LENGTH) {
      notificationService.warning('Por favor ingresa un código de 6 dígitos');
      return;
    }

    if (isCodeExpired()) {
      notificationService.warning(
        'El código ha expirado. Por favor solicita uno nuevo'
      );
      return;
    }

    if (!tempToken) {
      notificationService.error('No hay token disponible');
      return;
    }

    setIsLoading(true);
    try {
      await authService.verify2FACode(code, tempToken);
      clearCountdown();
      notificationService.success('Código verificado correctamente');
      // Refresh auth context to get the final user without temp_token
      await refreshAuth();
      onVerificationSuccess();
    } catch (error: any) {
      notificationService.error(
        error.message || 'Código de verificación inválido'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async (method: 'sms' | 'email') => {
    if (!canResendCode() || !tempToken) {
      return;
    }

    setIsSendingCode(true);
    try {
      await authService.send2FACode({type: method}, tempToken);
      const methodDisplay = method === 'sms' ? 'SMS' : 'Email';
      notificationService.success(
        `Código reenviado exitosamente por ${methodDisplay}`
      );
      setCodeSended(true);
      setCode(''); // Clear the input field
      startCountdown();
    } catch (error: any) {
      notificationService.error(
        error.message || 'Error al reenviar el código'
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleBackToLogin = async () => {
    await logout();
    onBackToLogin();
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled">
        <View style={styles.content}>
          {/* Logo Section */}
          <View style={styles.logoContainer}>
            <View style={styles.logoRow}>
              <Text style={styles.logoTextSan}>Care</Text>
              <Text style={styles.logoTextJuan}>Badge</Text>
            </View>
            <View style={styles.logoUnderline} />
            <Text style={styles.logoTextCare}>Platform</Text>
            <Text style={styles.tagline}>HEALTHCARE MANAGEMENT</Text>
          </View>

          {/* Security Icon */}
          <View style={styles.securityIconContainer}>
            <Icon name="security" size={64} color="#6366F1" />
          </View>

          {/* Title Section */}
          <View style={styles.titleContainer}>
            <Text style={styles.title}>Two-Factor Authentication</Text>
            <Text style={styles.subtitle}>
              Ingresa el código de verificación de 6 dígitos que te enviamos
            </Text>
          </View>

          {/* Form Section */}
          <View style={styles.form}>
            {/* Send Code Section - Visible when code not sent */}
            {!codeSended && (
              <View style={styles.sendCodeSection}>
                <Text style={styles.sectionTitle}>
                  ¿Cómo deseas recibir el código?
                </Text>

                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    (isSendingCode || isLoading) && styles.buttonDisabled,
                  ]}
                  onPress={() => send2FACode('sms')}
                  disabled={isSendingCode || isLoading}>
                  <View style={styles.methodContent}>
                    <Icon name="phone" size={24} color="#6366F1" />
                    <View style={styles.methodTextContainer}>
                      <Text style={styles.methodTitle}>Enviar por SMS</Text>
                      <Text style={styles.methodSubtitle}>
                        Código por mensaje de texto
                      </Text>
                    </View>
                  </View>
                  {isSendingCode && (
                    <ActivityIndicator size="small" color="#6366F1" />
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    (isSendingCode || isLoading) && styles.buttonDisabled,
                  ]}
                  onPress={() => send2FACode('email')}
                  disabled={isSendingCode || isLoading}>
                  <View style={styles.methodContent}>
                    <Icon name="email" size={24} color="#6366F1" />
                    <View style={styles.methodTextContainer}>
                      <Text style={styles.methodTitle}>Enviar por Email</Text>
                      <Text style={styles.methodSubtitle}>
                        Código por correo electrónico
                      </Text>
                    </View>
                  </View>
                  {isSendingCode && (
                    <ActivityIndicator size="small" color="#6366F1" />
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* Code Input Section - Visible when code sent */}
            {codeSended && (
              <View style={styles.codeInputSection}>
                <View style={styles.codeInputHeader}>
                  <Text style={styles.inputLabel}>Código de Verificación</Text>
                  {timeRemaining > 0 ? (
                    <View style={styles.timerContainer}>
                      <Icon name="access-time" size={16} color="#666" />
                      <Text style={styles.timerText}>{getFormattedTime()}</Text>
                    </View>
                  ) : isCodeExpired() ? (
                    <View style={styles.expiredContainer}>
                      <Icon name="warning" size={16} color="#F44336" />
                      <Text style={styles.expiredText}>Código expirado</Text>
                    </View>
                  ) : null}
                </View>

                <TextInput
                  ref={codeInputRef}
                  style={[
                    styles.codeInput,
                    isCodeExpired() && styles.codeInputExpired,
                  ]}
                  placeholder="000000"
                  placeholderTextColor="#999"
                  value={code}
                  onChangeText={text => {
                    // Only allow digits and limit to 6 characters
                    const cleanedText = text.replace(/[^0-9]/g, '').slice(0, 6);
                    setCode(cleanedText);
                  }}
                  keyboardType="number-pad"
                  maxLength={CODE_LENGTH}
                  editable={!isCodeExpired() && !isLoading}
                  autoFocus={codeSended}
                />

                {isCodeExpired() && (
                  <Text style={styles.expiredMessage}>
                    El código de verificación ha expirado. Por favor solicita
                    uno nuevo.
                  </Text>
                )}
              </View>
            )}

            {/* Resend Code Options - Show when code expired or can resend */}
            {codeSended && canResendCode() && (
              <View style={styles.resendSection}>
                <Text style={styles.resendTitle}>
                  {isCodeExpired()
                    ? 'Solicitar nuevo código'
                    : 'Reenviar código'}
                </Text>
                <Text style={styles.resendSubtitle}>
                  Elige cómo deseas recibir el código
                </Text>

                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    (isSendingCode || isLoading) && styles.buttonDisabled,
                  ]}
                  onPress={() => handleResendCode('sms')}
                  disabled={isSendingCode || isLoading}>
                  <View style={styles.methodContent}>
                    <Icon name="phone" size={24} color="#6366F1" />
                    <View style={styles.methodTextContainer}>
                      <Text style={styles.methodTitle}>Enviar por SMS</Text>
                      <Text style={styles.methodSubtitle}>
                        Código por mensaje de texto
                      </Text>
                    </View>
                  </View>
                  {isSendingCode && (
                    <ActivityIndicator size="small" color="#6366F1" />
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    (isSendingCode || isLoading) && styles.buttonDisabled,
                  ]}
                  onPress={() => handleResendCode('email')}
                  disabled={isSendingCode || isLoading}>
                  <View style={styles.methodContent}>
                    <Icon name="email" size={24} color="#6366F1" />
                    <View style={styles.methodTextContainer}>
                      <Text style={styles.methodTitle}>Enviar por Email</Text>
                      <Text style={styles.methodSubtitle}>
                        Código por correo electrónico
                      </Text>
                    </View>
                  </View>
                  {isSendingCode && (
                    <ActivityIndicator size="small" color="#6366F1" />
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* Verify Button */}
            {codeSended && !isCodeExpired() && (
              <TouchableOpacity
                style={[
                  styles.verifyButton,
                  (isLoading || code.length !== CODE_LENGTH) &&
                    styles.buttonDisabled,
                ]}
                onPress={handleVerifyCode}
                disabled={isLoading || code.length !== CODE_LENGTH}>
                <LinearGradient
                  colors={['#6366F1', '#8B5CF6']}
                  start={{x: 0, y: 0}}
                  end={{x: 1, y: 0}}
                  style={styles.buttonGradient}>
                  {isLoading ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={styles.buttonText}>Verificar Código</Text>
                  )}
                </LinearGradient>
              </TouchableOpacity>
            )}

            {/* Footer Actions - mismo estilo que Información del servicio */}
            <View style={styles.footerActions}>
              <ActionButton
                icon="arrow-back"
                label="Volver al login"
                variant="outlined"
                onPress={handleBackToLogin}
              />
            </View>

            {/* Copyright */}
            <Text style={styles.copyright}>
              Care Badge 2025 | REMA LLC - v.1.5.6
            </Text>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
  },
  content: {
    width: '100%',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 30,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  logoTextSan: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#1E3A8A',
  },
  logoTextJuan: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#3B82F6',
    marginLeft: 4,
  },
  logoUnderline: {
    width: 120,
    height: 1,
    backgroundColor: '#E5E7EB',
    alignSelf: 'center',
    marginTop: -4,
    marginBottom: 4,
  },
  logoTextCare: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#10B981',
    textAlign: 'center',
  },
  tagline: {
    fontSize: 12,
    color: '#6B7280',
    letterSpacing: 1,
    marginTop: 8,
    fontWeight: '500',
  },
  securityIconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  form: {
    width: '100%',
  },
  sendCodeSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 16,
  },
  methodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
  },
  methodContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  methodTextContainer: {
    marginLeft: 16,
  },
  methodTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  methodSubtitle: {
    fontSize: 14,
    color: '#6B7280',
  },
  codeInputSection: {
    marginBottom: 24,
  },
  resendSection: {
    marginBottom: 24,
  },
  resendTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  resendSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  codeInputHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  timerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timerText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 4,
  },
  expiredContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  expiredText: {
    fontSize: 14,
    color: '#F44336',
    marginLeft: 4,
  },
  codeInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    textAlign: 'center',
    letterSpacing: 8,
  },
  codeInputExpired: {
    borderColor: '#F44336',
    backgroundColor: '#FFEBEE',
  },
  expiredMessage: {
    fontSize: 12,
    color: '#F44336',
    marginTop: 8,
  },
  verifyButton: {
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 24,
  },
  buttonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  footerActions: {
    alignSelf: 'stretch',
    marginBottom: 32,
  },
  copyright: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 8,
  },
});

